import { useCallback, useEffect, useMemo, useState } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { DragNDropFileUploader, IFileSettings } from 'core-components/library/dragNDropFileUploader'
import { calculateFileLink } from 'core/classes/helpers/saveFiles'
import { IPartnerContact } from 'partner360/features/contacts'
import { TRANSLATION_KEYS } from 'partner360/features/contacts/model/constants'

import { InitialsPlaceholder } from './InitialsPlaceholder'

import styles from './ContactImageUploader.module.scss'

interface IContactImageUploaderProps {
  contact: IPartnerContact
  onImageUpdate: (files: IFileSettings) => void
  onDeleteImage: () => void
  isContactEditing?: boolean
}

export const ContactImageUploader = ({
  contact,
  onImageUpdate,
  onDeleteImage,
  isContactEditing = false,
}: IContactImageUploaderProps) => {
  const [image, setImage] = useState<string>()

  const storedImage = useMemo(() => {
    if (!contact.Image) {
      return
    }

    return calculateFileLink({
      entryId: contact.EntryId,
      formId: 700,
      fieldName: 'Image',
      fileName: contact.Image,
      UID: contact.UID,
    })
  }, [contact.EntryId, contact.Image, contact.UID])

  const displayImage = image || storedImage
  const hasImage = !!displayImage

  const uploadAttachments = useCallback(
    async (filesInfo: IFileSettings[]) => {
      const [imageData] = filesInfo
      setImage(imageData.value)
      onImageUpdate(imageData)
    },
    [onImageUpdate]
  )

  const deleteImage = useCallback(() => {
    setImage(undefined)
    onDeleteImage()
  }, [onDeleteImage])

  useEffect(() => {
    return () => {
      setImage(undefined)
    }
  }, [isContactEditing])

  if (!isContactEditing) {
    return (
      <div className={styles.root}>
        <div className={styles.customContent}>
          <div className={styles.imageContainer}>
            <RenderWhen condition={!!storedImage}>
              <img className={styles.avatar} src={storedImage} />
            </RenderWhen>
            <RenderWhen condition={!storedImage}>
              <InitialsPlaceholder
                firstName={contact.FirstName}
                lastName={contact.LastName}
                fullName={contact.FullName}
                className={styles.imagePlaceholder}
              />
            </RenderWhen>
          </div>
        </div>
        <div className={styles.deleteWrapper} />
      </div>
    )
  }

  return (
    <DragNDropFileUploader
      fileListVisibility={false}
      rootStyleVisible={false}
      className={styles.root}
      changeFileLoader={uploadAttachments}
      content={({ openFileList }) => {
        return (
          <div className={styles.customContentWrapper}>
            <div className={styles.customContent}>
              <div className={styles.imageContainer}>
                <RenderWhen condition={!!displayImage}>
                  <img src={displayImage} className={styles.avatar} />
                </RenderWhen>
                <RenderWhen condition={!displayImage && !isContactEditing}>
                  <InitialsPlaceholder
                    hideInitials={isContactEditing}
                    firstName={contact.FirstName}
                    lastName={contact.LastName}
                    fullName={contact.FullName}
                    className={styles.imagePlaceholder}
                  />
                </RenderWhen>
              </div>
              <RenderWhen condition={!hasImage}>
                <Button variant="outlined" onClick={openFileList} className={styles.button}>
                  <Translation text={TRANSLATION_KEYS.imageUploader.browse} />
                </Button>
              </RenderWhen>
            </div>
            <div className={styles.deleteWrapper}>
              <RenderWhen condition={isContactEditing && hasImage}>
                <Button
                  variant="text"
                  color="danger"
                  iconButton="kbase kbase-delete"
                  className={styles.deleteButton}
                  onClick={deleteImage}
                />
              </RenderWhen>
            </div>
          </div>
        )
      }}
    />
  )
}
